import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import PlatformResultCard from "@/components/PlatformResultCard";
import { Clock, RotateCcw, ChevronRight, X } from "lucide-react";

export default function HistoryTab({ onRerun }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/history").then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const open = async (id) => {
    const r = await http.get(`/history/${id}`);
    setSelected(r.data);
  };

  const rerun = () => {
    if (!selected) return;
    onRerun && onRerun(selected.input);
    setSelected(null);
  };

  return (
    <div className="space-y-5">
      <div className="tt-card p-6">
        <h1 className="text-3xl font-black h-tight">History</h1>
        <p className="text-sm text-zinc-500 mt-1">Every generation and optimization you've run. Click any row to see the full output — and re-run it with one click.</p>
      </div>

      <div className="tt-card p-2">
        {loading && <div className="p-4 text-sm text-zinc-500">Loading…</div>}
        {!loading && items.length === 0 && <div className="p-6 text-sm text-zinc-500">No runs yet.</div>}
        {items.map(it => (
          <button
            key={it.id}
            data-testid={`history-row-${it.id}`}
            onClick={() => open(it.id)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg hover:bg-zinc-50 transition-colors text-left"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className={"inline-block w-2 h-2 rounded-full " + (it.type === "generate" ? "bg-lime" : "bg-zinc-800")} />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{it.preview || it.input?.productName || (it.type === "optimize" ? "Optimize run" : "Generation")}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-2">
                  <Clock size={11} />{new Date(it.created_at).toLocaleString()}
                  <span className="text-zinc-300">·</span>
                  <span className="uppercase tracking-widest text-[10px] font-bold">{it.type}</span>
                  {it.input?.platforms?.length ? (
                    <><span className="text-zinc-300">·</span><span>{it.input.platforms.length} platform{it.input.platforms.length>1?"s":""}</span></>
                  ) : null}
                </div>
              </div>
            </div>
            <ChevronRight size={16} className="text-zinc-400" />
          </button>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6" onClick={()=>setSelected(null)}>
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{selected.type}</div>
                <div className="text-xl font-extrabold h-tight truncate">{selected.preview || selected.input?.productName || "Run detail"}</div>
              </div>
              <div className="flex items-center gap-2">
                {selected.type === "generate" && (
                  <button data-testid="history-rerun-btn" onClick={rerun} className="btn-primary inline-flex items-center gap-1 text-sm">
                    <RotateCcw size={14} /> Load into Generate
                  </button>
                )}
                <button onClick={()=>setSelected(null)} className="p-2 rounded-full hover:bg-zinc-100" data-testid="history-close-btn"><X size={18} /></button>
              </div>
            </div>
            <div className="p-6 overflow-auto">
              {selected.type === "generate" && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {(selected.output?.listings || []).map(l => (
                    <PlatformResultCard key={l.platform} listing={l} />
                  ))}
                </div>
              )}
              {selected.type === "optimize" && (
                <pre className="text-xs bg-zinc-50 p-4 rounded-lg overflow-auto">{JSON.stringify(selected.output, null, 2)}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
